import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_06: GgxTourDefinition = {
    key: '[RISK-06] Measuring Toxicity',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 6,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Will the chatbot ever say something it should not?',
        subtitle:
            'LLMs learn from enormous amounts of text, and a lot of that text is offensive, ' +
            'biased or hateful. A model can reproduce those patterns without anyone intending ' +
            'it to. Toxicity ranges from the obvious (slurs, threats, sexual or violent ' +
            'content) to the subtle (sarcasm, microaggressions, a hostile tone), and for a ' +
            'customer-facing bank chatbot any of it is a serious problem. This tour covers the ' +
            'kinds of toxicity to watch for, how guardrails detect them, how you test for ' +
            'them, and then opens the toxicity report on the pipeline.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen why toxicity shows up in LLMs and the seven kinds of harmful ' +
            'output to watch for, from explicit slurs to tone that slowly escalates. You saw ' +
            'the three detection layers guardrails use (rules and patterns, embeddings and ' +
            'classifiers, and an LLM judge for the subtle cases), the three levels of testing ' +
            '(your own use-case data, stress-test datasets, red-teaming), and how the toxicity ' +
            'report scores every reply on each dimension and compares it with what the ' +
            'customer said. Next in this track, Mitigating Risks through Guardrails in the ' +
            'Learning Hub shows how input and output guardrails stop harmful behaviour before ' +
            'it reaches anyone.',
        learned: [
            'There are **seven kinds of harmful output**, from slurs to escalating tone.',
            'Detection runs in **three layers**: rules, classifiers, an LLM judge.',
            'The **toxicity report** scores every reply on each dimension.',
        ],
    },
    chapters: [
        {
            name: 'Find the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'search',
            steps: [2, 3, 4],
        },
        {
            name: 'What toxicity looks like',
            blurb: 'Categories, detection layers, and how to test',
            icon: 'compass',
            steps: [5, 6, 7],
        },
        {
            name: 'The toxicity report',
            blurb: 'A real assessment on the pipeline dashboard',
            icon: 'grid',
            steps: [8, 9, 10],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: 'Start from the search box',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will end on a real toxicity report, so let us find the pipeline first. The ' +
                'global search finds any object by name from any page, and ' +
                '<strong>Ctrl+K</strong> opens it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            title: 'Seven kinds of harmful output',
            whatHappens: 'next, how guardrails detect them.',
            icon: 'compass',
            description:
                'Toxicity is not one thing. These are the categories a model has to be guarded ' +
                'against, and each is a place where a policy can be broken:' +
                '<ul data-ggx-list>' +
                '<li><strong>Hate and harassment</strong>: racial, caste-based, ethnic, ' +
                'gendered or personal attacks.</li>' +
                '<li><strong>Violence and threats</strong>: violent language, intimidation, ' +
                'encouraging self-harm.</li>' +
                '<li><strong>Sexual content</strong>: explicit or adult content, ' +
                'sexualisation of minors.</li>' +
                '<li><strong>Illegality and dangerous acts</strong>: guidance on drugs, ' +
                'weapons, crime or evasion.</li>' +
                '<li><strong>Abusive or aggressive tone</strong>: rude, insulting, or ' +
                'escalation-prone dialogue.</li>' +
                '<li><strong>Implicit or borderline toxicity</strong>: sarcasm, coded ' +
                'language, microaggressions.</li>' +
                '<li><strong>Sensitive requests</strong>: self-harm, health misinformation, ' +
                'anything needing high caution.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">A credit card bot rarely produces the ' +
                'obvious categories. Its real exposure is tone: a customer who is angry about ' +
                'a fee, and a reply that mirrors the anger instead of de-escalating.</aside>' +
                'Click Continue.',
        },
        {
            title: 'How guardrails catch it',
            whatHappens: 'next, how you test that they work.',
            icon: 'compass',
            description:
                'Three detection layers, working together, and the graphic lays them out:' +
                '<ul data-ggx-list>' +
                '<li><strong>Rules and pattern filters</strong> are the quick checks: ' +
                'expletives and toxic words, threatening or violent expressions, profanity ' +
                'lists and protected-class references.</li>' +
                '<li><strong>Embeddings and classifiers</strong> detect the common flavours ' +
                '(hate, harassment, threats, sexual content) by semantic similarity, plus ' +
                'dangerous topics like bomb-making or hacking.</li>' +
                '<li>An <strong>LLM-as-a-Judge</strong> catches what the first two miss: ' +
                'sarcasm and irony, subtle harassment, harmful or unsafe guidance, and ' +
                'borderline statements whose harm depends on reading them carefully.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">The same three layers guard against ' +
                'vulnerability and toxicity. What changes is the word lists, the classifier ' +
                'and the rubric the judge is given.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-06-guardrail-methods.png',
                title: 'Common methods used by guardrails to promote safety',
                caption: 'Rules and patterns, embeddings and classifiers, and LLM-as-a-judge.',
            },
        },
        {
            title: 'Three levels of testing',
            whatHappens: 'next, the dashboard where the toxicity report lives.',
            icon: 'compass',
            description:
                'Start with what you already have and work outward. The graphic shows the ' +
                'three levels:' +
                '<ul data-ggx-list>' +
                '<li><strong>Existing use-case data</strong>: take transcripts with a harmful ' +
                'tone, rude phrasing or borderline content, mix in a random sample of normal ' +
                'traffic, and measure the toxicity score distribution, false negatives and ' +
                'false positives by category.</li>' +
                '<li><strong>Stress-test data</strong>: adversarial datasets built to break ' +
                'your guardrails, augmented with public toxicity corpora and synthetic ' +
                'variants, and shaped to your architecture.</li>' +
                '<li><strong>Red-teaming</strong>: human or agentic testers run open-ended ' +
                'challenges that lean on escalation, emotional manipulation and multi-turn ' +
                'coercion.</li>' +
                '</ul>' +
                '<aside data-ggx-use="Tip">Test the toxicity guardrail on its own, without ' +
                'the full pipeline, so you know it blocks unsafe content reliably before you ' +
                'judge the pipeline that depends on it.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-06-toxicity-testing.png',
                title: 'Testing toxicity of the pipeline and the effectiveness of guardrails',
                caption: 'Existing use-case data, stress-test data, then red-teaming.',
            },
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'Where the reports live',
            whatHappens: 'the Dashboard opens on the latest run, with one tab per report.',
            icon: 'grid',
            description:
                'The latest evaluation run on this pipeline produced several reports: ' +
                'accuracy, faithfulness, stability, toxicity and vulnerability. Each is a ' +
                'different lens on quality and safety, and they are worth exploring in turn. ' +
                'Click <strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: '[test-id^="jobtab-Toxicity Report"]',
            scrollIntoView: true,
            title: 'Open the toxicity report',
            whatHappens: 'the Toxicity Report opens on its executive summary.',
            icon: 'grid',
            description:
                'The report tabs run across the top of the run. Click <strong>Toxicity ' +
                'Report (Credit Card Pipeline)</strong> to see how the model handles harmful ' +
                'and harmless inputs across all seven dimensions.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Toxicity Report (Credit Card Pipeline)',
        },
        {
            target: '[test-id="figure-Executive Summary"] h1',
            frame: true,
            scrollIntoView: true,
            title: 'Reading the toxicity report',
            whatHappens: 'the tour wraps up.',
            icon: 'grid',
            description:
                'This report scores every reply with an LLM-as-a-Judge, 0 to 4 on each ' +
                'toxicity dimension, and deliberately includes toxic customer messages to see ' +
                'whether the bot mirrors them or stays calm. The <strong>executive summary' +
                '</strong> explains the method and how to read the indicators. Scroll down for ' +
                'the rest:' +
                '<ul data-ggx-list>' +
                '<li><strong>Toxicity reduction analysis</strong>: input and output scores side ' +
                'by side across the seven dimensions.</li>' +
                '<li><strong>Distribution charts</strong>: whether the bot consistently ' +
                'de-escalates.</li>' +
                '<li><strong>Per-intent breakdown</strong>: where tone slips by question ' +
                'type.</li>' +
                '<li><strong>Full evaluation table</strong>: every input and output pair with ' +
                'its scores.</li>' +
                '</ul>' +
                'Reports and metrics can be shaped to your own safety policy. Click Continue ' +
                'to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
